import DashboardService from "@/services/api/dashboard";
import { handleResponse } from "@/services/axios";
import type {
  DashboardData,
  DashboardState,
  WishesState,
} from "@/types/dashboardtypes";
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

// ---------------------
// Initial State
// ---------------------
const initialState: DashboardState = {
  isFetching: false,
  data: {
    TotalSales: 0,
    TotalUnitSales: 0,
    TotalVisits: 0,
    TotalCalls: 0,
    TotalLeadsAdded: 0,
    TotalOpportunitiesValue: 0,
    PeopleCheckedIn: 0,
    TotalExpenseClaims: 0,
    TotalTeamMembers: 0,
    TotalLRMLogs: 0,
    TotalIdle: 0,
    TotalLeaves: 0,
    TotalVisitForm: 0,
    TotalDynamicForm: 0,
    TotalJoinVisit: 0,
    salesReport: [],
    visitReport: [],
    callingReport: [],
    leadReport: [],
    opportunityTeamReport: [],
    expenseReport: [],
    expenseCategoryReport: [],
    opportunityValueReport: [],
    opportunityCountReport: [],
    customerCountReport: [],
    wishes: { birthday: [], anniversary: [] },
    geoUserList: [],
    customerVisitList: [],
    jointVisitList: [],
    userIdelList: [],
    teamLeaveList: [],
    dynamicVisitFormlist: [],
    detailCallViewList: [],
  },
};

// ---------------------
// Generic Report Thunk Creator
// ---------------------
const createReportThunk = (name: string, serviceMethod: Function) =>
  createAsyncThunk<any[], any, { rejectValue: string }>(
    name,
    async (params, { rejectWithValue }) => {
      try {
        const response = await serviceMethod(params);
        return handleResponse(response, rejectWithValue);
      } catch (error: any) {
        return rejectWithValue(error.message);
      }
    }
  );

// ---------------------
// Async Thunks
// ---------------------
export const getDashboardStats = createAsyncThunk<
  Partial<DashboardData>,
  void,
  { rejectValue: string }
>("dashboard/getStats", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getDashboardStats();
    return response.data ?? {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getDashboardSalesReport = createReportThunk(
  "dashboard/salesReport",
  DashboardService.getDashboardSalesReport
);
export const getDashboardVisitReport = createReportThunk(
  "dashboard/visitReport",
  DashboardService.getDashboardVisitReport
);
export const getDashboardCallingReport = createReportThunk(
  "dashboard/callingReport",
  DashboardService.getDashboardCallingReport
);
export const getDashboardLeadsReport = createReportThunk(
  "dashboard/leadsReport",
  DashboardService.getDashboardLeadsReport
);
export const getDashboardOpportunityTeamReport = createReportThunk(
  "dashboard/opportunityTeamReport",
  DashboardService.getDashboardOpportunityTeamReport
);
export const getDashboardExpenseReport = createReportThunk(
  "dashboard/expenseReport",
  DashboardService.getDashboardExpenseReport
);
export const getDashboardExpenseCategoryReport = createReportThunk(
  "dashboard/expenseCategoryReport",
  DashboardService.getDashboardExpenseCategoryReport
);
export const getDashboardOpportunityValueReport = createReportThunk(
  "dashboard/opportunityValueReport",
  DashboardService.getDashboardOpportunityValueReport
);
export const getDashboardOpportunityCountReport = createReportThunk(
  "dashboard/opportunityCountReport",
  DashboardService.getDashboardOpportunityCountReport
);
export const getDashboardCustomerCountReport = createReportThunk(
  "dashboard/customerCountReport",
  DashboardService.getDashboardCustomerCountReport
);

export const getTodaysWishes = createAsyncThunk<
  WishesState,
  void,
  { rejectValue: string }
>("dashboard/todaysWishes", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getTodaysWishes();
    return response as WishesState;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const sendWishes = createAsyncThunk<
  { response: any; wishType: "birthday" | "anniversary"; index: number },
  { wishType: "birthday" | "anniversary"; userId: number; index: number },
  { rejectValue: string }
>("dashboard/sendWish", async (data, { rejectWithValue }) => {
  try {
    const response = await DashboardService.sendWishes(data);
    return { response, ...data };
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GeoUserDetail = createReportThunk(
  "dashboard/geoUserDetail",
  DashboardService.GeoUserDetail
);
export const GetAllCustomerViewReport = createReportThunk(
  "dashboard/GetAllCustomerViewReport",
  DashboardService.GetAllCustomerViewReport
);
export const GetAllCustomerJointVisitReport = createReportThunk(
  "dashboard/GetAllCustomerJointVisitReport",
  DashboardService.GetAllCustomerJointVisitReport
);
export const GetIdleDetails = createReportThunk(
  "dashboard/GetIdleDetails",
  DashboardService.GetIdleDetails
);
export const GetTeamLeaveDetails = createReportThunk(
  "dashboard/GetTeamLeaveDetails",
  DashboardService.GetTeamLeaveDetails
);
export const DynamicVisitForm = createReportThunk(
  "dashboard/DynamicVisitForm",
  DashboardService.DynamicVisitForm
);
export const GetCallView = createReportThunk(
  "dashboard/GetCallView",
  DashboardService.GetCallView
);

// ---------------------
// Slice
// ---------------------
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    const setPending = (state: DashboardState) => {
      state.isFetching = true;
    };
    const setRejected = (state: DashboardState) => {
      state.isFetching = false;
    };

    builder
      .addCase(getDashboardStats.pending, setPending)
      .addCase(
        getDashboardStats.fulfilled,
        (state, action: PayloadAction<Partial<DashboardData>>) => {
          state.isFetching = false;
          state.data = { ...state.data, ...action.payload };
        }
      )
      .addCase(getDashboardStats.rejected, setRejected);

    const reports: [any, keyof DashboardState["data"]][] = [
      [getDashboardSalesReport, "salesReport"],
      [getDashboardVisitReport, "visitReport"],
      [getDashboardCallingReport, "callingReport"],
      [getDashboardLeadsReport, "leadReport"],
      [getDashboardOpportunityTeamReport, "opportunityTeamReport"],
      [getDashboardExpenseReport, "expenseReport"],
      [getDashboardExpenseCategoryReport, "expenseCategoryReport"],
      [getDashboardOpportunityValueReport, "opportunityValueReport"],
      [getDashboardOpportunityCountReport, "opportunityCountReport"],
      [getDashboardCustomerCountReport, "customerCountReport"],
      [GetAllCustomerViewReport, "customerVisitList"],
      [GeoUserDetail, "geoUserList"],
      [GetAllCustomerJointVisitReport, "jointVisitList"],
      [GetIdleDetails, "userIdelList"],
      [GetTeamLeaveDetails, "teamLeaveList"],
      [DynamicVisitForm, "dynamicVisitFormlist"],
      [GetCallView, "detailCallViewList"],
    ];

    reports.forEach(([thunk, key]) => {
      builder.addCase(thunk.pending, setPending);
      builder.addCase(
        thunk.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.isFetching = false;
          (state.data as any)[key] = action.payload;
        }
      );
      builder.addCase(thunk.rejected, setRejected);
    });

    // Wishes
    builder
      .addCase(getTodaysWishes.pending, setPending)
      .addCase(
        getTodaysWishes.fulfilled,
        (state, action: PayloadAction<WishesState>) => {
          state.isFetching = false;
          state.data.wishes = action.payload;
        }
      )
      .addCase(getTodaysWishes.rejected, setRejected)
      .addCase(sendWishes.pending, setPending)
      .addCase(sendWishes.fulfilled, (state, action) => {
        state.isFetching = false;
        const { wishType, index, response } = action.payload;

        if (response?.Status === 200) {
          const list = state.data.wishes?.[wishType];
          
          if (list && list[index]) {
            list[index].IsWished = true;
          }
        }
      })
      .addCase(sendWishes.rejected, setRejected);
  },
});

export const { clear } = dashboardSlice.actions;
export default dashboardSlice.reducer;
